import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { MemberNameWithPositions } from '@/components/shared/member-name-with-positions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface UnitMember {
  id: string;
  memberId: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  contactHidden?: boolean;
  memberType: string;
  currentTermRoles?: string[];
}

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  contactHidden?: boolean;
  memberType: string;
}

interface DepartmentUnitMembersPanelProps {
  departmentId: string;
  unitId: string;
  canEdit: boolean;
}

const personLabel = (person: Pick<Person, 'firstName' | 'lastName'>): string =>
  `${person.firstName} ${person.lastName}`.trim();

export const DepartmentUnitMembersPanel = ({
  departmentId,
  unitId,
  canEdit,
}: DepartmentUnitMembersPanelProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const [removeTarget, setRemoveTarget] = useState<UnitMember | null>(null);

  const basePath = `/people/departments/${departmentId}/units/${unitId}`;

  const members = useQuery({
    queryKey: ['people', 'departments', departmentId, 'units', unitId, 'members'],
    queryFn: () => api.get<{ data: UnitMember[] }>(`${basePath}/members?pageSize=100`),
  });

  const people = useQuery({
    queryKey: ['people', 'members', 'unit-picker', memberQuery],
    queryFn: () =>
      api.get<{ data: Person[] }>(
        `/people/members?pageSize=20${memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''}`,
      ),
    enabled: addOpen,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (addOpen) return;
    setSelectedPeople([]);
    setMemberQuery('');
    setPickedId(null);
  }, [addOpen]);

  const memberRows = members.data?.data ?? [];
  const memberIds = useMemo(() => new Set(memberRows.map((row) => row.memberId)), [memberRows]);
  const selectedIds = useMemo(
    () => new Set(selectedPeople.map((person) => person.id)),
    [selectedPeople],
  );

  const pickerOptions = useMemo(
    () =>
      (people.data?.data ?? [])
        .filter((person) => !memberIds.has(person.id) && !selectedIds.has(person.id))
        .map((person) => ({
          id: person.id,
          label: personLabel(person),
          description: person.phone ?? undefined,
        })),
    [memberIds, people.data?.data, selectedIds],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'units', unitId, 'members'],
    });
    void queryClient.refetchQueries({
      queryKey: ['people', 'departments', departmentId, 'units'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'insights'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'members'],
    });
    void queryClient.invalidateQueries({ queryKey: ['people', 'departments'] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'members'] });
  };

  const addMembers = useMutation({
    mutationFn: async () => {
      for (const person of selectedPeople) {
        await api.post(`${basePath}/members`, { memberId: person.id });
      }
      return selectedPeople.length;
    },
    onSuccess: (count) => {
      toast.success(t('departments.units.members.saved', { count }));
      setAddOpen(false);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) => api.delete(`${basePath}/members/${memberId}`),
    onSuccess: () => {
      toast.success(t('departments.units.members.removed'));
      setRemoveTarget(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<UnitMember>[] = [
    {
      id: 'name',
      header: t('departments.units.members.columns.name'),
      cell: (row) => (
        <MemberNameWithPositions
          memberId={row.memberId}
          name={personLabel(row)}
          positions={row.currentTermRoles}
        />
      ),
    },
    {
      id: 'phone',
      header: t('departments.units.members.columns.phone'),
      cell: (row) =>
        row.contactHidden ? t('members.contactHidden') : (row.phone ?? '—'),
    },
    {
      id: 'type',
      header: t('departments.units.members.columns.type'),
      cell: (row) => t(`members.type.${row.memberType}` as 'members.type.member'),
    },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: tCommon('table.actions'),
            align: 'right' as const,
            cell: (row: UnitMember) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  setRemoveTarget(row);
                }}
              >
                {tCommon('action.remove')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  const selectedColumns: DataTableColumn<Person>[] = [
    {
      id: 'name',
      header: t('departments.units.members.columns.name'),
      cell: (row) => <MemberNameWithPositions memberId={row.id} name={personLabel(row)} />,
    },
    {
      id: 'phone',
      header: t('departments.units.members.columns.phone'),
      cell: (row) =>
        row.contactHidden ? t('members.contactHidden') : (row.phone ?? '—'),
    },
    {
      id: 'actions',
      header: tCommon('table.actions'),
      align: 'right',
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() =>
            setSelectedPeople((current) => current.filter((person) => person.id !== row.id))
          }
        >
          {t('departments.addMembers.removeSelected')}
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-body-sm font-medium text-fg">{t('departments.units.members.title')}</h4>
          <p className="mt-1 text-body-sm text-fg-secondary">
            {t('departments.units.members.description')}
          </p>
        </div>
        {canEdit ? (
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
            {t('departments.units.members.add')}
          </Button>
        ) : null}
      </div>

      <DataTable
        rows={memberRows}
        columns={columns}
        rowKey={(row) => row.memberId}
        loading={members.isLoading}
        empty={
          <p className="text-body-sm text-fg-secondary">{t('departments.units.members.empty')}</p>
        }
      />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent size="xl" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('departments.units.members.addTitle')}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              addMembers.mutate();
            }}
          >
            <DialogBody className="overflow-visible" contentClassName="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('departments.units.members.addDescription')}
              </p>
              <div className="flex flex-col gap-1.5">
                <Label>{t('departments.units.members.people')}</Label>
                <EntityCombobox
                  value={pickedId}
                  onChange={(id) => {
                    if (!id) {
                      setPickedId(null);
                      return;
                    }
                    const person = people.data?.data.find((row) => row.id === id);
                    if (!person || memberIds.has(id) || selectedIds.has(id)) {
                      setPickedId(null);
                      return;
                    }
                    setSelectedPeople((current) => [...current, person]);
                    setPickedId(null);
                  }}
                  options={pickerOptions}
                  onQueryChange={setMemberQuery}
                  loading={people.isFetching}
                  placeholder={t('departments.units.members.placeholder')}
                  emptyMessage={t('departments.units.members.pickerEmpty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('departments.units.members.selected')}</Label>
                <DataTable
                  rows={selectedPeople}
                  columns={selectedColumns}
                  rowKey={(person) => person.id}
                  empty={
                    <p className="text-body-sm text-fg-secondary">
                      {t('departments.units.members.selectedEmpty')}
                    </p>
                  }
                />
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2 pt-3">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={addMembers.isPending}
                disabled={selectedPeople.length === 0}
              >
                {t('departments.units.members.confirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRemoveTarget(null);
        }}
        title={t('departments.units.members.removeTitle', {
          name: removeTarget ? personLabel(removeTarget) : '',
        })}
        description={t('departments.units.members.removeDescription')}
        confirmLabel={tCommon('action.remove')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeMember.isPending}
        onConfirm={() => {
          if (removeTarget) removeMember.mutate(removeTarget.memberId);
        }}
      />
    </div>
  );
};
